import { Controller } from "@hotwired/stimulus";

// **LA RECHERCHE DU BACK-OFFICE** — prototype « Console & Éditorial », §4 : *« la recherche
// est un routeur »*.
//
// 🔴 **Le filtrage est CLIENT, et c'est mesuré, pas préféré.** L'index fait au plus 36 lignes,
// il est rendu par le serveur DÉJÀ borné par la porte du rôle, et il ne bouge pas pendant la
// session. Une requête par frappe serait un aller-retour pour une liste qu'on tient dans la
// page.
//
// ⚠️ **Ce contrôleur ne DÉCIDE de rien.** Il ne connaît ni les droits, ni les routes : il
// masque des lignes que le serveur a écrites. Un écran que le rôle n'ouvre pas n'est pas dans
// le DOM — il ne peut donc pas remonter d'une faute de filtre.
//
// ⚠️ **Les mots cherchés sont dans `data-haystack`**, écrit par le serveur en minuscules. Lire
// `textContent` aurait cherché dans ce qui est AFFICHÉ, et le sous-titre ne l'est pas toujours.
export default class extends Controller {
  static targets = ["input", "panel", "hit", "none"];
  static values = { empty: String };

  connect() {
    // ⚠️ **Fermer au clic extérieur, jamais au `blur`** : un `blur` se déclenche avant le
    // `click` sur un résultat, et le panneau se fermait sous le doigt — le lien n'était
    // jamais suivi.
    this.closeOutside = (event) => {
      if (!this.element.contains(event.target)) this.close();
    };
    this.closeOnEscape = (event) => {
      if (event.key === "Escape") this.close();
    };
    document.addEventListener("click", this.closeOutside);
    document.addEventListener("keydown", this.closeOnEscape);
  }

  disconnect() {
    document.removeEventListener("click", this.closeOutside);
    document.removeEventListener("keydown", this.closeOnEscape);
  }

  open() {
    this.panelTarget.hidden = false;
  }

  close() {
    this.panelTarget.hidden = true;
  }

  // 🔴 **Le même repli d'accents que le serveur** — voir
  // `BackofficeBarHelper#go4learn_backoffice_haystack`. Replier un seul côté ne trouve rien.
  static fold(texte) {
    return texte.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
  }

  filter() {
    const query = this.constructor.fold(this.inputTarget.value.trim());
    let shown = 0;

    this.hitTargets.forEach((hit) => {
      // ⚠️ **Tous les mots, dans n'importe quel ordre** : « audit journal » doit trouver le
      // journal d'audit. Une recherche de sous-chaîne entière ne l'aurait pas fait.
      const haystack = hit.dataset.haystack || "";
      const match = query === "" || query.split(/\s+/).every((word) => haystack.includes(word));
      hit.hidden = !match;
      if (match) shown += 1;
    });

    // 🔴 **L'état vide NOMME des exemples réels** — cahier §4. Un « aucun résultat » sec
    // laisse l'utilisateur deviner ce que la recherche indexe.
    this.noneTarget.hidden = shown !== 0;
    this.noneTarget.textContent = shown === 0 ? this.emptyValue : "";
    this.open();
  }
}
